import React from 'react';
import { BarChart3, Plus } from 'lucide-react';
import { APP_BTN_PRIMARY_CLASS } from '../../../ui/controlStyles';
import { TASK_VIEWS, type TaskViewId } from '../taskViews';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { Kbd, TASK_COMPACT_TRIGGER_CLASS, TASK_SECTION_LABEL_CLASS } from './taskUi';

const ROW_CLASS = 'flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm transition-colors';
const ROW_IDLE = 'font-normal text-app-text-muted hover:bg-app-bg';
const ROW_ACTIVE = 'bg-inbox-selected-strong font-semibold text-inbox-selected-text';

type Props = {
  active: TaskViewId | 'team';
  counts: Record<TaskViewId, number>;
  /** Team-Übersicht (Owner/Admin mit „alle ansehen“) */
  isAdmin: boolean;
  /** „Alle offenen“ nur mit Recht „alle ansehen“ */
  showAllView: boolean;
  canEdit: boolean;
  onSelect: (view: TaskViewId | 'team') => void;
  onCreate: () => void;
};

export function TaskViewNav({ active, counts, isAdmin, showAllView, canEdit, onSelect, onCreate }: Props) {
  return (
    <aside className="app-scrollbar flex w-[216px] shrink-0 flex-col gap-5 overflow-y-auto border-r border-app-border bg-app-surface-muted px-2.5 py-4">
      {canEdit && <NewTaskButton onClick={onCreate} className="w-full" />}
      <nav aria-label="Ansichten" className="flex flex-col gap-0.5">
        <div className={`px-2 pb-1.5 ${TASK_SECTION_LABEL_CLASS}`}>Ansichten</div>
        {TASK_VIEWS.filter((v) => showAllView || v.id !== 'all').map((v) => {
          const isActive = active === v.id;
          const n = counts[v.id];
          return (
            <button
              key={v.id}
              type="button"
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onSelect(v.id)}
              className={`${ROW_CLASS} ${isActive ? ROW_ACTIVE : ROW_IDLE}`}
            >
              <span className="flex-1 truncate">{v.label}</span>
              {n > 0 && (
                <span
                  className={`text-xs tabular-nums ${
                    v.id === 'overdue' ? 'font-medium text-red-600' : isActive ? '' : 'text-app-text-subtle'
                  }`}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {isAdmin && (
        <div className="flex flex-col gap-0.5">
          <div className={`px-2 pb-1.5 ${TASK_SECTION_LABEL_CLASS}`}>Verwaltung</div>
          <button
            type="button"
            aria-current={active === 'team' ? 'page' : undefined}
            onClick={() => onSelect('team')}
            className={`${ROW_CLASS} ${active === 'team' ? ROW_ACTIVE : ROW_IDLE}`}
          >
            <BarChart3 className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
            <span className="flex-1 truncate">Team-Übersicht</span>
          </button>
        </div>
      )}

      {/* Am unteren Rand der Spalte — Nachschlagehilfe, nicht Navigation */}
      <div className="mt-auto flex flex-col gap-0.5">
        <div className={`px-2 pb-1.5 ${TASK_SECTION_LABEL_CLASS}`}>Tastenkürzel</div>
        {canEdit && <ShortcutRow label="Neues To-do" keys={['N']} />}
        <ShortcutRow label="Auswahl bewegen" keys={['↑', '↓']} />
        <ShortcutRow label="Öffnen" keys={['Enter']} />
        {canEdit && <ShortcutRow label="Erledigen" keys={['X']} />}
        <ShortcutRow label="Detail schließen" keys={['Esc']} />
      </div>
    </aside>
  );
}

/** Ersatz für die linke Spalte bei schmalen Fenstern. */
export function TaskViewSelect({
  active,
  isAdmin,
  showAllView,
  onSelect,
}: {
  active: TaskViewId | 'team';
  isAdmin: boolean;
  showAllView: boolean;
  onSelect: (view: TaskViewId | 'team') => void;
}) {
  const options = [
    ...TASK_VIEWS.filter((v) => showAllView || v.id !== 'all').map((v) => ({ value: v.id, label: v.label })),
    ...(isAdmin ? [{ value: 'team', label: 'Team-Übersicht' }] : []),
  ];
  return (
    <AppSelectDropdown
      value={active}
      onChange={(v) => onSelect(v as TaskViewId | 'team')}
      options={options}
      className="w-44"
      triggerClassName={TASK_COMPACT_TRIGGER_CLASS}
    />
  );
}

/** Mobil: Ansichten als waagerecht wischbare Chips mit Zähler. */
export function TaskViewChips({
  active,
  counts,
  isAdmin,
  showAllView,
  onSelect,
}: {
  active: TaskViewId | 'team';
  counts: Record<TaskViewId, number>;
  isAdmin: boolean;
  showAllView: boolean;
  onSelect: (view: TaskViewId | 'team') => void;
}) {
  const ref = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    ref.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);

  const teamActive = active === 'team';
  return (
    <nav
      ref={ref}
      aria-label="Ansichten"
      className="-mx-4 flex items-center gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {/* Team-Übersicht vorne und abgesetzt — hinten läge sie unsichtbar hinter dem rechten Rand */}
      {isAdmin && (
        <>
          <button
            type="button"
            aria-current={teamActive ? 'page' : undefined}
            onClick={() => onSelect('team')}
            className={`flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-sm font-medium transition-colors ${
              teamActive
                ? 'border-transparent bg-pipedrive-blue text-white'
                : 'border-pipedrive-blue/40 bg-app-surface text-pipedrive-blue'
            }`}
          >
            <BarChart3 className="h-4 w-4" strokeWidth={2} />
            Team
          </button>
          <span aria-hidden className="mx-0.5 h-5 w-px shrink-0 bg-app-border" />
        </>
      )}
      {TASK_VIEWS.filter((v) => showAllView || v.id !== 'all').map((v) => {
        const isActive = active === v.id;
        const count = counts[v.id];
        return (
          <button
            key={v.id}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onSelect(v.id)}
            className={`flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-sm transition-colors ${
              isActive
                ? 'border-transparent bg-inbox-selected-strong font-semibold text-inbox-selected-text'
                : 'border-app-border bg-app-surface text-app-text-muted'
            }`}
          >
            {v.label}
            {count > 0 && (
              <span
                className={`text-xs tabular-nums ${
                  v.id === 'overdue' ? 'font-medium text-red-600' : isActive ? '' : 'text-app-text-subtle'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

export function NewTaskButton({ onClick, className = '' }: { onClick: () => void; className?: string }) {
  return (
    <button type="button" onClick={onClick} className={`${APP_BTN_PRIMARY_CLASS} ${className}`}>
      <Plus className="h-4 w-4" strokeWidth={2} />
      Neues To-do
    </button>
  );
}

function ShortcutRow({ label, keys }: { label: string; keys: string[] }) {
  return (
    <div className="flex items-center justify-between px-2 py-1 text-xs text-app-text-subtle">
      <span>{label}</span>
      <span className="flex gap-1">
        {keys.map((k) => (
          <Kbd key={k}>{k}</Kbd>
        ))}
      </span>
    </div>
  );
}
